import { faSave, faTrash } from "@fortawesome/free-solid-svg-icons";
import type { WirelessSurveyOut } from "kubb";
import type { WirelessSurveyTarget } from "kubb";
import { useMemo, useState } from "react";
import * as yup from "yup";
import BulkUpdateCSVDropZone from "../../../components/BulkUpdate/BulkUpdateCSVDropZone";
import UpdateButton from "../../../components/Buttons/UpdateButton";
import CompanyNavLink from "../../../components/navigation/CompanyNavLink";
import { useUpdateWirelessSurvey } from "../../../hooks/updateEntity.tsx";
import CleanupWarningBanner from "../assetPositions/simpleAssetRegisterUpload/CleanupWarningBanner";
import {
  type UploadDefinition,
  convertCSVToDefinitions,
} from "../assetPositions/simpleAssetRegisterUpload/GenericUploadPage";
import PadControls from "../assetPositions/simpleAssetRegisterUpload/PadControls";
import UploadPreviewTable from "../assetPositions/simpleAssetRegisterUpload/UploadPreviewTable";
import {
  type PadConfig,
  applyPadToDefinition,
} from "../assetPositions/simpleAssetRegisterUpload/padSerialNumber";

type ParsedTargetDefinition = UploadDefinition<WirelessSurveyTarget>;

// Yup keeps the field as a plain required string so apostrophe-wrapped /
// whitespace-padded values reach applyPadToDefinition, which mirrors the
// backend's _strip_apostrophes + strip_whitespace cleanup. Shape validation
// (regex, formula-injection ban, length cap) is the backend's job via
// SerialNumberStr in management-api/src/schema/asset/common.py.
// The CSV header (and the kubb-generated WirelessSurveyTarget shape) uses
// snake_case "sort_order"; an earlier camelCase "sortOrder" key here meant
// the integer cast never ran on the parsed value.
const importSchema = yup.object().shape({
  serial_number: yup.string().required(),
  label: yup.string(),
  sort_order: yup
    .number()
    .transform((_, val) => (val !== "" ? Number(val) : null))
    .nullable(true)
    .integer(),
});

function WirelessSurveyTargetTab(props: {
  survey: WirelessSurveyOut;
  refresh?: () => any;
}) {
  const [newTargets, setNewTargets] = useState<
    ParsedTargetDefinition[] | undefined
  >(undefined);
  const [padConfig, setPadConfig] = useState<PadConfig>({
    enabled: true,
    length: 8,
  });

  const currentNumberOfTargets =
    props.survey.configuration?.targets?.length || 0;

  // Apply serial-number cleanup + optional padding on every render so the
  // preview live-updates when the user toggles the pad checkbox or edits
  // the length. The same paddedTargets is used for the submit payload, so
  // what the user sees in the table is exactly what gets saved.
  const paddedTargets = useMemo(
    () => newTargets?.map((t) => applyPadToDefinition(t, padConfig)),
    [newTargets, padConfig],
  );

  function processFile(data: Promise<ParsedTargetDefinition[]>) {
    data.then(setNewTargets);
  }

  async function postUpload() {
    setNewTargets(undefined);
    props.refresh?.();
  }

  const update = useUpdateWirelessSurvey(props.survey);

  return (
    <>
      <p>This survey currently has {currentNumberOfTargets} targets</p>
      <p>
        You can change the targets for the survey by uploading a new CSV file.
        Once uploaded, you will be shown a preview before you save the changes.
      </p>
      <p>
        Your csv file must contain the columns:
        <ul>
          <li>
            <b>serial_number</b> - The 8 digit secondary address for the device
            you want to find
          </li>
        </ul>
      </p>
      <p>
        Your csv file may contain the columns:
        <ul>
          <li>
            <b>label</b> - A description you want to give to the device
          </li>
          <li>
            <b>sort_order</b> - An integer to set the default sort order of
            targets
          </li>
        </ul>
      </p>
      <p>
        <CompanyNavLink
          to={{ pathname: "/examples/wireless_survey_targets.csv" }}
          onlyExternal={true}
        >
          Click to download an example file.
        </CompanyNavLink>
      </p>

      <BulkUpdateCSVDropZone
        buttonLabel={"Upload new target CSV"}
        parseComplete={(d) => {
          processFile(convertCSVToDefinitions(d, importSchema));
        }}
      />
      <UpdateButton
        text={"Save new targets"}
        updatedEntity={{
          ...props.survey,
          configuration: {
            targets: paddedTargets
              ?.filter((v) => (v.errors || []).length === 0)
              .map((v) => v.object) as WirelessSurveyTarget[],
          },
        }}
        update={update}
        icon={faSave}
        color={"brandLight"}
        disabled={paddedTargets === undefined}
        postUpdate={postUpload}
      />
      <UpdateButton
        text={"Delete current targets"}
        update={update}
        updatedEntity={{ ...props.survey, configuration: { targets: [] } }}
        icon={faTrash}
        color={"red"}
        disabled={currentNumberOfTargets === 0}
        postUpdate={props.refresh}
      />
      {paddedTargets && (
        <>
          <PadControls padConfig={padConfig} setPadConfig={setPadConfig} />
          <CleanupWarningBanner definitions={paddedTargets} />
          <UploadPreviewTable
            definitions={paddedTargets}
            requiredHeaders={["serial_number"]}
            optionalHeaders={["label", "sort_order"]}
            uploadedEntityName={"target"}
            trafficLight={true}
          />
        </>
      )}
    </>
  );
}

export default WirelessSurveyTargetTab;
